/**
 * ============================================================================
 * RecurringDiscountCard - Read-Only Display of Court Recurring Discounts
 * ============================================================================
 *
 * Displays recurring booking discount configuration for a single court.
 * Read-only component with link to Courts tab for editing.
 *
 * ============================================================================
 */

import React from 'react';
import { Calendar, ExternalLink, BarChart3 } from 'lucide-react';
import type { Court } from '../../types/courts';
import { useNavigate } from 'react-router-dom';

interface RecurringDiscountCardProps {
  court: Court;
}

export function RecurringDiscountCard({ court }: RecurringDiscountCardProps) {
  const navigate = useNavigate();

  // MVP: Only show 1 and 3 month discounts (hiding 6 & 12 for now)
  const hasDiscounts =
    court.recurring_discount_1_month > 0 ||
    court.recurring_discount_3_months > 0;

  const handleNavigateToCourt = () => {
    navigate('/courts');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
            <h3 className="font-bold text-sm sm:text-base text-gray-900 truncate">{court.name}</h3>
            {!court.is_active && (
              <span className="px-1.5 sm:px-2 py-0.5 bg-red-100 text-red-700 text-[10px] sm:text-xs font-bold rounded flex-shrink-0">
                Inactive
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 truncate">
            {court.sport?.name || 'No sport assigned'}
          </p>
        </div>
      </div>

      {/* Discount Display - MVP: Only 1 & 3 months */}
      {hasDiscounts ? (
        <div className="space-y-2 mb-3 sm:mb-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            {/* 1 Month */}
            <div className="bg-blue-50 rounded-lg p-2 sm:p-2.5 border border-blue-100">
              <p className="text-[10px] sm:text-xs text-blue-600 font-semibold mb-0.5">1 Month</p>
              <p className="text-base sm:text-lg font-black text-blue-900">
                {court.recurring_discount_1_month}%
              </p>
            </div>

            {/* 3 Months */}
            <div className="bg-green-50 rounded-lg p-2 sm:p-2.5 border border-green-100">
              <p className="text-[10px] sm:text-xs text-green-600 font-semibold mb-0.5">3 Months</p>
              <p className="text-base sm:text-lg font-black text-green-900">
                {court.recurring_discount_3_months}%
              </p>
            </div>
          </div>

          {/* Info: 2-month coming soon */}
          <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
            <p className="text-[10px] sm:text-xs text-gray-600 text-center">
              ℹ️ 2-month bookings available (discount not configured)
            </p>
          </div>

          {/* Usage Analytics Placeholder */}
          <div className="bg-blue-50 rounded-lg p-2 sm:p-2.5 border border-blue-100">
            <div
              className="flex items-center justify-center text-blue-600 cursor-help"
              title="Usage tracking coming soon - will show active recurring bookings count"
            >
              <BarChart3 className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5" />
              <span className="text-[10px] sm:text-xs font-semibold">
                Usage Analytics: Coming Soon
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4 border border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600 text-center">
            No recurring discounts configured
          </p>
        </div>
      )}

      {/* Edit Button */}
      <button
        onClick={handleNavigateToCourt}
        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-700 rounded-lg text-xs sm:text-sm font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
      >
        <span>Edit in Courts Tab</span>
        <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
      </button>
    </div>
  );
}
